import React, { useState } from "react";
import { Box, Button, Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel, Typography } from "@mui/material";

import { LayerIds } from "fond/types";
import { ConfirmModal } from "fond/widgets";

import { DataPrepGenerateOptions } from "./dataPrepRunParameters";

interface RunDataPrepProps {
  /**
   * Layers present in the version, used to detect data that generating would replace
   */
  layerKeys: string[];
  /**
   * Whether every other precondition for running data prep is met
   */
  canRun: boolean;
  isRerun: boolean;
  readOnly: boolean;
  generate: DataPrepGenerateOptions;
  onGenerateChange: (generate: DataPrepGenerateOptions) => void;
  onRun: () => void;
}

const RunDataPrep: React.FC<RunDataPrepProps> = ({ layerKeys, canRun, isRerun, readOnly, generate, onGenerateChange, onRun }: RunDataPrepProps) => {
  const [isConfirmingReplace, setIsConfirmingReplace] = useState<boolean>(false);

  const noGenerationSelected = !generate.underground && !generate.leadins;
  const existingUnderground = layerKeys.includes(LayerIds.inStreet) || layerKeys.includes(LayerIds.inStreetPoints);
  const existingLeadins = layerKeys.includes(LayerIds.inLeadIns);
  const replacesUnderground = generate.underground && existingUnderground;
  const replacesLeadins = generate.leadins && existingLeadins;
  const replacesExistingData = replacesUnderground || replacesLeadins;

  const replacedLayerNames: string[] = [];
  if (replacesUnderground) replacedLayerNames.push("underground path and points");
  if (replacesLeadins) replacedLayerNames.push("leadins");

  const handleRun = () => {
    if (replacesExistingData) {
      setIsConfirmingReplace(true);
    } else {
      onRun();
    }
  };

  const handleConfirmReplace = () => {
    setIsConfirmingReplace(false);
    onRun();
  };

  return (
    <>
      <Box border={1} borderColor="action.focus" borderRadius={1} p={1} sx={{ mb: 1, "&:last-child": { mb: 0 } }}>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" focused={false} sx={{ fontSize: (theme) => theme.typography.size.small, mb: 0.5 }}>
            Choose what to generate
          </FormLabel>
          <FormGroup>
            <FormControlLabel
              label="Underground path and points"
              slotProps={{ typography: { variant: "body2" } }}
              control={
                <Checkbox
                  size="small"
                  sx={{ py: 0.5 }}
                  checked={generate.underground}
                  onChange={(event) => onGenerateChange({ ...generate, underground: event.target.checked })}
                />
              }
              disabled={readOnly}
            />
            <FormControlLabel
              label="Leadins"
              slotProps={{ typography: { variant: "body2" } }}
              control={
                <Checkbox
                  size="small"
                  sx={{ py: 0.5 }}
                  checked={generate.leadins}
                  onChange={(event) => onGenerateChange({ ...generate, leadins: event.target.checked })}
                />
              }
              disabled={readOnly}
            />
          </FormGroup>
          <Button
            fullWidth
            variant="contained"
            size="small"
            sx={{ mt: 1 }}
            disabled={!canRun || noGenerationSelected || readOnly}
            onClick={handleRun}
          >
            {isRerun ? "Re-run data preparation" : "Run data preparation"}
          </Button>
        </FormControl>
      </Box>

      {isConfirmingReplace && replacesExistingData && (
        <ConfirmModal
          open
          disabled={!canRun || readOnly}
          header="Replace existing data?"
          confirmText="Continue"
          cancelText="Cancel"
          color="error"
          content={
            <Typography variant="body2">
              This will replace existing layer data ({replacedLayerNames.join(", ")}). This action cannot be undone.
            </Typography>
          }
          onConfirm={handleConfirmReplace}
          onCancel={() => setIsConfirmingReplace(false)}
        />
      )}
    </>
  );
};

export default RunDataPrep;
